import { useCallback, useEffect, useState } from 'react';
import {
  buildCssVars,
  normalizeSettings,
  resolveThemeName,
  type LapoSettings,
} from '@lapo/ui';
import { applyDesktopWindowBackground } from './shell/windowChrome.js';

/**
 * Owns app settings: loads from localStorage, persists every change, and applies the two settings
 * with DOM side effects — the theme (CSS custom properties on <html>, following the OS when set to
 * `system`) and the user's custom CSS (a single injected <style>). The settings UI stays
 * presentational; this is where the IO lives.
 */
const STORAGE_KEY = 'lapo-settings';
const CUSTOM_CSS_ID = 'lapo-custom-css';

function load(): LapoSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return normalizeSettings(raw ? JSON.parse(raw) : undefined);
  } catch {
    return normalizeSettings(undefined);
  }
}

/** Write the resolved theme's CSS vars onto the document root. */
function applyTheme(settings: LapoSettings, prefersDark: boolean): void {
  const name = resolveThemeName(settings.theme, prefersDark);
  const vars = buildCssVars(name, { grayscale: settings.grayscale });
  const root = document.documentElement;
  for (const [k, v] of Object.entries(vars)) root.style.setProperty(k, v);
  root.dataset.theme = name;
  // Keep the native desktop window background in step with the surface, so the macOS overlay
  // title-bar strip doesn't flash white in dark mode. No-op on web/mobile.
  applyDesktopWindowBackground(vars['--lapo-bg']);
}

/** Inject (or update) the single custom-CSS <style> element. */
function applyCustomCss(css: string): void {
  let el = document.getElementById(CUSTOM_CSS_ID) as HTMLStyleElement | null;
  if (!css.trim()) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('style');
    el.id = CUSTOM_CSS_ID;
    document.head.appendChild(el);
  }
  el.textContent = css;
}

export interface UseSettings {
  settings: LapoSettings;
  /** Shallow-merge a patch and persist. */
  update: (patch: Partial<LapoSettings>) => void;
}

export function useSettings(): UseSettings {
  const [settings, setSettings] = useState<LapoSettings>(load);

  // Persist on every change.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {
      /* storage unavailable (private mode); keep running in-memory */
    }
  }, [settings]);

  // Apply theme, and re-apply when the OS preference flips while on `system`.
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    applyTheme(settings, mq.matches);
    if (settings.theme !== 'system') return;
    const onChange = () => applyTheme(settings, mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [settings.theme, settings.grayscale]);

  // Apply custom CSS.
  useEffect(() => {
    applyCustomCss(settings.customCss);
  }, [settings.customCss]);

  const update = useCallback((patch: Partial<LapoSettings>) => {
    setSettings((prev) => ({ ...prev, ...patch }));
  }, []);

  return { settings, update };
}
